import { useEffect, useState } from "react";
import DashboardShell, { StatCard } from "@/components/DashboardShell";
import { api, fmtNGN, fmtBps } from "@/lib/api";
import { Link } from "react-router-dom";
import { ArrowRight } from "@phosphor-icons/react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from "recharts";

export default function AdminDashboard() {
  const [k, setK] = useState(null);
  const [txns, setTxns] = useState([]);
  useEffect(() => {
    api.get("/admin/kpis").then((r) => setK(r.data));
    api.get("/admin/transactions").then((r) => setTxns(r.data));
  }, []);

  if (!k) return <DashboardShell title="Admin"><p>Loading…</p></DashboardShell>;

  const day = {};
  for (let i = 13; i >= 0; i--) { const d = new Date(); d.setDate(d.getDate() - i); day[d.toISOString().slice(0, 10)] = { gmv: 0, fees: 0 }; }
  txns.forEach((t) => { const x = (t.created_at || "").slice(0, 10); if (day[x]) { day[x].gmv += t.amount; day[x].fees += t.platform_fee; } });
  const series = Object.entries(day).map(([d, v]) => ({ day: d.slice(5), gmv: v.gmv, fees: v.fees }));

  return (
    <DashboardShell title="Platform overview" subtitle="Live GMV, take rate, loan book and commission ledger.">
      <div className="grid lg:grid-cols-4 gap-6 mb-8">
        <StatCard label="GMV processed" value={fmtNGN(k.gmv)} sub={`${k.txn_count} transactions`} accent="primary" />
        <StatCard label="Platform revenue" value={fmtNGN(k.platform_revenue)} sub={`Take rate ${fmtBps(k.take_rate_bps)}`} accent="accent" />
        <StatCard label="Loan book" value={fmtNGN(k.loan_book)} sub={`${k.loan_count} loans · NPL ${k.npl_ratio}%`} accent="warning" />
        <StatCard label="Float balance" value={fmtNGN(k.float_balance)} sub={`Spread ${fmtBps(k.fee_config.wallet_float_spread_bps)} APR`} accent="success" />
      </div>

      <div className="grid lg:grid-cols-4 gap-6 mb-8">
        <StatCard label="Schools" value={k.schools} accent="primary" />
        <StatCard label="Parents" value={k.parents} accent="primary" />
        <StatCard label="Agents (DSA)" value={k.agents} accent="primary" />
        <StatCard label="Pending loans" value={k.pending_loans} sub="Awaiting decision" accent="warning" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-8">
        <div className="card lg:col-span-2">
          <h3 className="font-display text-lg font-medium mb-4">GMV & fees · last 14 days</h3>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E3DB" />
              <XAxis dataKey="day" stroke="#4F5D4F" fontSize={12} />
              <YAxis stroke="#4F5D4F" fontSize={12} />
              <Tooltip />
              <Line type="monotone" dataKey="gmv" stroke="#0A3622" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="fees" stroke="#F2B632" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="card">
          <h3 className="font-display text-lg font-medium mb-4">DSA commissions</h3>
          <div className="space-y-3 text-sm">
            <Row k="Pending" v={fmtNGN(k.pending_commissions)} />
            <Row k="Paid lifetime" v={fmtNGN(k.paid_commissions)} />
            <Row k="DSA cut" v={fmtBps(k.fee_config.dsa_commission_bps)} />
          </div>
          <Link to="/admin/users" className="btn-outline mt-5 !py-2 !px-4 text-xs" data-testid="admin-manage-agents">Manage agents <ArrowRight size={12} /></Link>
        </div>
      </div>

      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-display text-lg font-medium">Live commercial config</h3>
          <Link to="/admin/fee-config" className="text-sm text-[#F2B632] hover:underline" data-testid="edit-fee-config-link">Edit <ArrowRight size={12} className="inline" /></Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 text-sm">
          <Row k="Txn fee" v={fmtBps(k.fee_config.platform_txn_fee_bps)} />
          <Row k="Late fee" v={fmtBps(k.fee_config.platform_late_fee_bps)} />
          <Row k="DSA commission" v={fmtBps(k.fee_config.dsa_commission_bps)} />
          <Row k="Float spread" v={fmtBps(k.fee_config.wallet_float_spread_bps)} />
          <Row k="Loan rate (def.)" v={fmtBps(k.fee_config.default_loan_interest_bps)} />
        </div>
      </div>
    </DashboardShell>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between items-baseline border-b border-[#E5E3DB] py-2"><span className="text-[#4F5D4F]">{k}</span><span className="font-mono font-semibold text-[#132A13]">{v}</span></div>;
}
